/*
 * Copyright (C) 2023 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { InjectionToken } from '@angular/core';
import { MembersBooleanSearchRequest } from '@app/app/shared/models/admin/boolean-filters.model';
import { MembersSearchResponse } from '@app/app/shared/models/admin/members.model';
import { Observable } from 'rxjs';
import { Page } from '../../../shared/models/page';
import { LearnerPlaylistSummary } from '../../../shared/models/playlist/learner-playlist-summary.model';
import { SetPlaylistSkillsRequest } from '../../../shared/models/skills/skills.model';
import { ObservableResult } from '../../../shared/store';
import {
  CourseAwardConfiguration,
  CourseAwardCriteriaEvaluationResults,
  DeepLinkingResponse,
  ImportedUserModel,
  LmsCourse,
  LtiCourse,
  LtiLinkableItem,
} from '../models/lti-course.model';

export const LTI_COURSE_DATA_SERVICE = new InjectionToken<LtiCourseDataService>('[Lti] LtiCourseDataService');

export const LTI_PROVIDER = 'lti_provider';
export const LTI_COURSE_UID = 'lti_course_uid';

/** The service handling LTI course CRUD operations. */
export interface LtiCourseDataService {
  getLtiCourse(requestId: string): ObservableResult<LtiCourse>;

  getLtiCourseByPlaylistUid(coursePlaylistUid: string): ObservableResult<LtiCourse>;

  createLtiCourse(requestId: string): ObservableResult<LtiCourse>;

  getLtiPlaylist(requestId: string, playlistUri: string): ObservableResult<LearnerPlaylistSummary>;

  getLtiPlaylists(requestId: string, page: number, size: number, term?: string): ObservableResult<Page<LearnerPlaylistSummary>>;

  getLinkableItems(playlistUid: string, courseUid: string): ObservableResult<LtiLinkableItem[]>;

  configureBadge(courseUid: string, badgeUid: string, awardConfiguration?: CourseAwardConfiguration): ObservableResult<null>;

  selectLtiPlaylist(requestId: string, playlistUid: string): ObservableResult<DeepLinkingResponse>;

  selectMultipleLtiPlaylists(requestId: string, playlists: string[]): ObservableResult<DeepLinkingResponse>;

  selectLtiCards(requestId: string, cards: string[]): ObservableResult<DeepLinkingResponse>;

  deeplinkSkills(request: SetPlaylistSkillsRequest, requestId: string): ObservableResult<DeepLinkingResponse>;

  checkLtiSkillsCriteria(
    organizationUid: string,
    coursePlaylistUid: string,
    userUid: string,
  ): Observable<CourseAwardCriteriaEvaluationResults>;

  submitLtiSkills(
    organizationUid: string,
    coursePlaylistUid: string,
    userUid: string,
  ): ObservableResult<CourseAwardCriteriaEvaluationResults>;

  searchPlaylistMembers(
    request: MembersBooleanSearchRequest,
    playlistUid: string,
    page: number,
    size: number,
    courseUid: string,
  ): ObservableResult<MembersSearchResponse>;

  exportPlaylistMembers(playlistUid: string, courseUid: string): ObservableResult<string>;

  importCourseParticipants(courseUid: string, badgeUid: string): ObservableResult<ImportedUserModel[]>;

  selectBadge(
    requestId: string,
    courseUid: string,
    badgeUid: string,
    awardConfiguration: CourseAwardConfiguration,
  ): ObservableResult<DeepLinkingResponse>;

  getLmsCourse(requestId: string): Observable<LmsCourse>;

  getLmsCourseByCourseUid(courseUid: string): Observable<LmsCourse>;

  getLmsCourseMembers(courseUid: string, includeBadges: boolean, includeSkills: boolean): Observable<ImportedUserModel[]>;
}
